import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SyncIcon from '@mui/icons-material/Sync';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { colors } from '../../../theme/tokens';
import { globalDriftApi, CatalogDrift, CatalogSyncResult } from '../../../api/driftApi';
import { WriteButton } from '../../../components/ui';

interface Props {
  open: boolean;
  onClose: () => void;
}

const headerSx = { fontSize: 22, fontWeight: 700, fontFamily: 'Manrope, sans-serif', color: colors.textPrimary };
const subSx = { fontSize: 13, color: colors.textSecondary, mt: 0.5 };

const errMessage = (e: unknown): string => {
  const anyE = e as any;
  return anyE?.response?.data?.message || anyE?.message || 'Something went wrong';
};

const GlobalDriftDialog: React.FC<Props> = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const [lastSync, setLastSync] = useState<CatalogSyncResult[] | null>(null);

  const driftQuery = useQuery({
    queryKey: ['global-drift'],
    queryFn: () => globalDriftApi.get(),
    enabled: open,
    staleTime: 15_000,
  });

  // pendingKey: undefined = not syncing; null = syncing ALL; string = single catalog.
  const [pendingKey, setPendingKey] = useState<string | null | undefined>(undefined);
  const syncMutation = useMutation({
    mutationFn: (catalogs?: string[]) => globalDriftApi.sync(catalogs),
    onSuccess: (data) => {
      setLastSync(data.synced.filter((s) => s.synced > 0));
      queryClient.setQueryData(['global-drift'], data.catalogs);
      // refresh the per-card counts on the landing page
      queryClient.invalidateQueries({ queryKey: ['admin', 'globals'] });
    },
    onSettled: () => setPendingKey(undefined),
  });

  const catalogs = (driftQuery.data || []) as CatalogDrift[];
  const totalMissing = catalogs.reduce((n, c) => n + c.missing, 0);

  const runSync = (key?: string) => {
    setLastSync(null);
    setPendingKey(key ?? null);
    syncMutation.mutate(key ? [key] : undefined);
  };

  const handleClose = () => {
    setLastSync(null);
    syncMutation.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogContent sx={{ p: 4 }}>
        <Typography sx={headerSx}>Global Catalog Drift</Typography>
        <Typography sx={subSx}>
          Platform globals not yet mirrored into <code>config</code> (which the import / publish validators
          read). Sync is <strong>add-only</strong> — it pulls missing rows in and never deletes or edits
          existing config rows.
        </Typography>

        {driftQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
        ) : driftQuery.isError ? (
          <Alert severity="error" sx={{ mt: 2 }}>Failed to load drift: {errMessage(driftQuery.error)}</Alert>
        ) : (
          <>
            {totalMissing === 0 && !lastSync && (
              <Alert icon={<CheckCircleOutlineIcon />} severity="success" sx={{ mt: 2 }}>
                All catalogs are in sync — nothing to pull from the platform.
              </Alert>
            )}
            {lastSync && (
              <Alert icon={<CheckCircleOutlineIcon />} severity="success" sx={{ mt: 2 }}>
                {lastSync.length === 0
                  ? 'Nothing to sync — already up to date.'
                  : `Synced: ${lastSync.map((s) => `${s.synced} ${s.label.toLowerCase()}`).join(', ')}.`}
              </Alert>
            )}
            {syncMutation.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>Sync failed: {errMessage(syncMutation.error)}</Alert>
            )}

            <Table size="small" sx={{ mt: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Catalog</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>Platform</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>Config</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>Missing</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {catalogs.map((c) => {
                  const syncing = syncMutation.isPending && (pendingKey === c.key || pendingKey === null);
                  return (
                    <TableRow key={c.key}>
                      <TableCell sx={{ fontSize: 13 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {c.label}
                          <Tooltip title={c.note}>
                            <Chip
                              label={c.strategy === 'full-mirror' ? 'mirror' : 'referenced'}
                              size="small"
                              sx={{ height: 18, fontSize: 10, bgcolor: colors.bgMuted, color: colors.textSecondary }}
                            />
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, color: colors.textSecondary }}>{c.platformTotal}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, color: colors.textSecondary }}>{c.configTotal}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 13 }}>
                        {c.missing > 0 ? (
                          <Box component="span" sx={{ fontWeight: 700, color: colors.warningText, bgcolor: colors.warningBgStrong, px: 0.75, py: 0.25, borderRadius: '4px' }}>
                            {c.missing}
                          </Box>
                        ) : (
                          <Box component="span" sx={{ color: colors.textTertiary }}>0</Box>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <WriteButton
                          ignoreEnvReadOnly
                          size="small"
                          disabled={c.missing === 0 || syncMutation.isPending}
                          startIcon={syncing ? <CircularProgress size={12} color="inherit" /> : <SyncIcon sx={{ fontSize: 14 }} />}
                          onClick={() => runSync(c.key)}
                          sx={{ textTransform: 'none', fontSize: 12 }}
                        >
                          Sync
                        </WriteButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.5 }}>
              <InfoOutlinedIcon sx={{ fontSize: 15, color: colors.textTertiary }} />
              <Typography sx={{ fontSize: 12, color: colors.textTertiary }}>
                "Platform" counts every non-deleted platform row. "Missing" is the syncable subset —
                full catalog for tasks, only rows referenced by a config tenant for the rest.
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 4, py: 2, borderTop: `1px solid ${colors.borderLight}` }}>
        <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Close</Button>
        <WriteButton
          ignoreEnvReadOnly
          variant="contained"
          disabled={totalMissing === 0 || syncMutation.isPending}
          startIcon={syncMutation.isPending && pendingKey === null ? <CircularProgress size={16} color="inherit" /> : <SyncIcon sx={{ fontSize: 16 }} />}
          onClick={() => runSync()}
          sx={{ textTransform: 'none', bgcolor: colors.brand, color: '#1a1a1a', '&:hover': { bgcolor: colors.brandHover } }}
        >
          Sync all ({totalMissing})
        </WriteButton>
      </DialogActions>
    </Dialog>
  );
};

export default GlobalDriftDialog;
